import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import MDSnackbar from 'src/components/md/MDSnackbar';

interface ToastContextType {
  showToast: (options: {
    message?: string;
    title?: string;
    color?: 'success' | 'info' | 'warning' | 'error';
    icon?: string;
    persistent?: boolean; // If true, toast stays until manually dismissed
  }) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<
    Parameters<ToastContextType['showToast']>[0]
  >({
    message: '',
    title: 'Success',
    color: 'success',
    icon: 'check',
    persistent: false,
  });

  useEffect(() => {
    if (open && !options.persistent) {
      // Auto-dismiss after 10 seconds (use persistent: true for longer messages)
      const timer = setTimeout(() => setOpen(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [open, options.persistent]);

  const showToast = (
    newOptions: Parameters<ToastContextType['showToast']>[0],
  ) => {
    setOptions({ ...options, ...newOptions });
    setOpen(true);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <MDSnackbar
        color={options.color}
        icon={options.icon}
        title={options.title}
        content={options.message}
        dateTime="Just now"
        open={open}
        onClose={() => setOpen(false)}
        close={() => setOpen(false)}
        bgWhite
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
